import { FiEye, FiEyeOff } from "react-icons/fi";
import Card from "../../../../../components/ui/Card";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Branch } from "../../../../../utils/supabaseClient";
import { ThemeType } from "../../../../../types/custom";
import { get } from "http";

type PreviewProps = {
	restaurant: Branch | null;
}

const Preview: React.FC<PreviewProps> = ({ restaurant }) => {
	const { t } = useTranslation();
	const [showPreview, setShowPreview] = useState(true);

	// Helper to safely cast theme to ThemeType
	const getTheme = () => restaurant?.theme as ThemeType;
	const isAddToCart = getTheme()?.showAddToCart ?? true;

	return <Card className="sticky top-6">
		<div className="flex items-center justify-between mb-4">
			<h3 className="text-lg font-medium text-gray-900 dark:text-white">
				{t('settings.livePreview')}
			</h3>
			<button
				onClick={() => setShowPreview(!showPreview)}
				className="p-2 text-gray-500 hover:text-gray-700 cursor-pointer"
			>
				{showPreview ? <FiEyeOff /> : <FiEye />}
			</button>
		</div>

		{showPreview && (
			<div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ">

				<div
					className="p-6 text-center"
					style={{
						backgroundColor: getTheme()?.backgroundColor,
						color: getTheme()?.textColor,
						fontFamily: getTheme()?.fontFamily,
					}}
				>
					{/* Header Preview */}
					<div
						className="p-4 mb-4 rounded-lg"
						style={{
							backgroundColor: getTheme()?.headerStyle === 'transparent' ? 'transparent' :
								getTheme()?.headerStyle === 'solid' ? getTheme()?.primaryColor : 'transparent',
							backgroundImage: getTheme()?.headerStyle === 'cover' ?
								`url('${restaurant?.cover_image}')` : '',
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							borderRadius: getTheme()?.borderRadius
						}}
					>
						<h4
							className={`${getTheme()?.titleFontSize} font-bold mb-1`}
							style={{
								fontFamily: getTheme()?.titleFontFamily,
								color: getTheme()?.headerStyle === 'solid' ? '#ffffff' : getTheme()?.textColor
							}}
						>
							{restaurant?.name ?? t('settings.restaurantName')}
						</h4>
						<p
							className="text-sm opacity-90"
							style={{
								color: getTheme()?.headerStyle === 'solid' ? '#ffffff' : getTheme()?.textColor
							}}
						>
							{restaurant?.description ?? t('settings.description')}
						</p>
					</div>
					{/* Menu Items Preview */}
					<div className={`grid gap-4 ${getTheme()?.menuStyle === 'grid' ? 'grid-cols-2' : 'grid-cols-1'}`}>
						{[1, 2, 3, 4].map((item) => (
							<div
								key={item}
								className={`p-3 text-start ${getTheme()?.cardStyle === 'minimal' ? 'border-0' :
									getTheme()?.cardStyle === 'shadow' ? 'shadow-md' :
										'border border-gray-200'
									}`}
								style={{
									backgroundColor: getTheme()?.cardColor,
									borderRadius: getTheme()?.borderRadius,
									borderColor: getTheme()?.borderColor
								}}
							>
								<div className="flex flex-col items-center gap-3 mb-2">
									<div
										className="w-8 h-8 rounded-full"
										style={{ backgroundColor: getTheme()?.accentColor }}
									/>
									<div className="flex-1">
										<h4 className={`${getTheme()?.boldTitles ? 'font-bold' : 'font-medium'} ${getTheme()?.itemFontSize}`}>Sample Dish {item}</h4>
										<p className="text-xs opacity-70">Delicious sample description</p>
									</div>
								</div>
								<div className="flex items-center justify-between flex-col ">
									<span
										className="text-sm font-bold"
										style={{ color: getTheme()?.primaryColor }}
									>
										$12.99
									</span>
									{getTheme()?.isAddToCart && (
										<button
											className="px-2 py-1 text-xs rounded"
											style={{
												backgroundColor: getTheme()?.primaryColor,
												color: '#ffffff',
												borderRadius: getTheme()?.borderRadius
											}}
										>
											{t('settings.preview.addToCart')}
										</button>
									)}
								</div>
							</div>
						))}
					</div>

					{/* Subcategory Tabs Preview */}
					<div className="mt-4 ">
						{getTheme()?.SubcategoryStyle === 'tabs' && (
							<div className="flex gap-2 justify-center">
								{['Appetizers', 'Main', 'Desserts'].map((cat, idx) => (
									<button
										key={cat}
										className={`px-3 py-1 text-xs rounded ${idx === 0 ? 'font-medium' : ''}`}
										style={{
											backgroundColor: idx === 0 ? getTheme()?.primaryColor : 'transparent',
											color: idx === 0 ? '#ffffff' : getTheme()?.textColor,
											borderRadius: getTheme()?.borderRadius,
											border: idx === 0 ? 'none' : `1px solid ${getTheme()?.borderColor}`
										}}
									>
										{cat}
									</button>
								))}
							</div>
						)}



						{getTheme()?.SubcategoryStyle === 'dropdown' && (
							<div className="w-full">
								<div
									className="relative w-full"
									style={{
										borderRadius: getTheme()?.borderRadius
									}}
								>
									<select
										className="w-full px-3 py-2 text-sm rounded border appearance-none cursor-pointer pr-8"
										style={{
											backgroundColor: getTheme()?.cardColor,
											color: getTheme()?.textColor,
											borderColor: getTheme()?.borderColor,
											borderRadius: getTheme()?.borderRadius
										}}
									>
										<option>Appetizers</option>
										<option>Main</option>
										<option>Desserts</option>
									</select>
									<div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
										<span
											className="text-xs"
											style={{ color: getTheme()?.textColor }}
										>
											▼
										</span>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		)}
	</Card >
};

export default Preview;